import { inject, Injectable } from '@angular/core';
import { Room } from '../../model/room';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  merge,
  Observable,
  of,
  scan,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';
import { Direction, EventType, M_POLL_START, MatrixEvent, RelationType, Room as MatrixRoom } from 'matrix-js-sdk';
import { decryptionUpdates$ } from '../sync/from-matrix-event';
import { SnackbarService } from '../snackbar/snackbar.service';
import { MatrixService } from '../matrix/matrix.service';
import { RoomListAccessService } from '../room-list/room-list-access.service';
import { RoomPowerLevelService } from '../room-power-level/room-power-level.service';
import { SyncService } from '../sync/sync.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Diese Serviceklasse dient zur Haltung der Methoden, die mit dem Anpinnen von Nachrichten zu tun haben.
 * Hier werden keine AFOs erfüllt, da die Anforderung aus dem Design kam.
 * Begründung: Hier werden keine AFOs erfüllt.
 */
export class PinnedMessageService {
  private readonly matrix = inject(MatrixService);
  private readonly syncService = inject(SyncService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly roomPowerLevelService = inject(RoomPowerLevelService);
  private readonly snackbarService = inject(SnackbarService);

  private readonly pinnedMessages$ = new BehaviorSubject<PinnedMessageDTO[]>([]);

  readonly pins$: Observable<PinnedMessageDTO[]> = this.getPins$().pipe(shareReplay(1));

  /********************************************************************************************************************
   * Public API
   ********************************************************************************************************************/

  /** Prüft, ob ein Event aktuell gepinnt ist. */
  isPinned(eventId: string | undefined): boolean {
    return !!eventId && this.pinnedMessages$.value.some(val => val.messageId === eventId);
  }

  /** Pinnt eine Nachricht im aktuellen Raum. Gibt false zurück bei erwartbaren Fehlschlägen. */
  async pinMessage(eventId: string | undefined): Promise<boolean> {
    if (!eventId) return false;

    const currRoom = this.roomListAccessService.getCurrentModelRoom();
    if (!currRoom) {
      throw new Error('pinMessage: Kein Raum ausgewählt');
    }

    if (!this.checkPinRights(currRoom, 'Sie haben nicht das Recht, eine Nachricht zu pinnen.')) return false;
    if (currRoom.pinnedEventIds.includes(eventId)) return false;

    const pinnedMessageIds = [...currRoom.pinnedEventIds, eventId];
    try {
      await this.sendPinState(currRoom.roomId, pinnedMessageIds);
      this.snackbarService.success('Nachricht wurde gepinnt');
      return true;
    } catch (error) {
      this.snackbarService.error('Nachricht konnte nicht gepinnt werden.');
      console.warn(`Nachricht ${eventId} konnte nicht gepinnt werden.`, error);
      return false;
    }
  }

  /** Löst eine gepinnte Nachricht. Gibt false zurück bei erwartbaren Fehlschlägen. */
  async unpinMessage(eventId: string | undefined): Promise<boolean> {
    if (!eventId) return false;

    const currRoom = this.roomListAccessService.getCurrentModelRoom();
    if (!currRoom) {
      throw new Error('unpinMessage: Kein Raum ausgewählt');
    }

    if (!this.checkPinRights(currRoom, 'Sie haben nicht das Recht, eine Nachricht zu lösen.')) return false;

    const pinnedMessageIds = currRoom.pinnedEventIds.filter(elem => elem !== eventId);
    try {
      await this.sendPinState(currRoom.roomId, pinnedMessageIds);
      this.snackbarService.success('Nachricht wurde gelöst');
      return true;
    } catch (error) {
      this.snackbarService.error('Nachricht konnte nicht gelöst werden.');
      console.warn(`Nachricht ${eventId} konnte nicht gelöst werden.`, error);
      return false;
    }
  }

  /********************************************************************************************************************
   * Reactive Pipeline
   ********************************************************************************************************************/

  /**
   * Hauptstream für gepinnte Nachrichten. Reagiert auf Raumwechsel (oder Änderungen an pinnedEventIds)
   * und delegiert an loadAndMapPinnedMessages$.
   * distinctUntilChanged verhindert, dass unrelated Room-Emissionen (Nachrichten, Receipts, etc.)
   * die laufende Pipeline canceln. tap aktualisiert pinnedMessages$ für synchronen Zugriff (isPinned).
   */
  private getPins$(): Observable<PinnedMessageDTO[]> {
    return this.roomListAccessService.getCurrentModelRoom$().pipe(
      filter(room => !!room),
      distinctUntilChanged(
        (prev, curr) =>
          prev.roomId === curr.roomId &&
          prev.pinnedEventIds.length === curr.pinnedEventIds.length &&
          prev.pinnedEventIds.every((id, i) => id === curr.pinnedEventIds[i])
      ),
      switchMap(room => {
        const pinnedEventIds = room.pinnedEventIds;
        if (pinnedEventIds.length === 0) {
          return of([] as PinnedMessageDTO[]);
        }

        const roomId = room.roomId;
        return this.loadAndMapPinnedMessages$(roomId, pinnedEventIds);
      }),
      tap(newPins => this.pinnedMessages$.next(newPins))
    );
  }

  /**
   * Fetcht alle gepinnten Events einmalig parallel und mappt sie zu DTOs.
   * Noch nicht entschlüsselte Events erzeugen per fromMatrixEvent einen Trigger,
   * der bei Entschlüsselung ein erneutes Mapping auslöst (in-place-Mutation auf dem MatrixEvent).
   * Edit-Events aktualisieren den betroffenen DTO direkt aus dem Edit-Event-Inhalt (applyEditToDto).
   * Cleanup erfolgt automatisch via switchMap-Teardown bei Raumwechsel.
   */
  private loadAndMapPinnedMessages$(roomId: string, pinnedMessageIds: string[]): Observable<PinnedMessageDTO[]> {
    return forkJoin(pinnedMessageIds.map(id => this.fetchPinnedEvent(roomId, id).pipe(catchError(() => EMPTY)))).pipe(
      switchMap(events => {
        const decryptionTriggers = events
          .filter(e => !e.getClearContent())
          .map(e => decryptionUpdates$(e).pipe(map(() => undefined as MatrixEvent | undefined)));

        const editTrigger$ = this.createEditTriggerForRoom$(roomId, pinnedMessageIds);

        return merge(of(undefined as MatrixEvent | undefined), ...decryptionTriggers, editTrigger$).pipe(
          scan((currentPins, editEvent) => {
            if (editEvent) {
              return this.applyEditToDto(currentPins, editEvent);
            }
            return events
              .map(e => this.eventToPinnedMessage(e))
              .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
          }, [] as PinnedMessageDTO[])
        );
      })
    );
  }

  /** Aktualisiert den betroffenen DTO direkt aus dem Edit-Event-Inhalt. */
  private applyEditToDto(currentPins: PinnedMessageDTO[], editEvent: MatrixEvent): PinnedMessageDTO[] {
    const targetId = editEvent.getContent()['m.relates_to']?.event_id;
    const newContent = (editEvent.getClearContent() ?? editEvent.getContent())?.['m.new_content'];
    if (!targetId || !newContent) {
      return currentPins;
    }
    return currentPins.map(pin =>
      pin.messageId === targetId
        ? {
            ...pin,
            body: newContent['body'] ?? newContent['formatted_body'] ?? pin.body,
            formattedBody: newContent['formatted_body'] ?? newContent['body'] ?? pin.formattedBody,
            msgType: newContent['msgtype'] ?? pin.msgType,
            mentions: newContent['m.mentions']?.user_ids ?? pin.mentions
          }
        : pin
    );
  }

  /** Emittiert das Edit-Event, wenn eine gepinnte Nachricht im Raum editiert wird (m.replace). */
  private createEditTriggerForRoom$(roomId: string, pinnedEventIds: string[]): Observable<MatrixEvent> {
    return this.syncService.message$.pipe(
      filter((event): boolean => {
        const relatesTo = event.getContent()['m.relates_to'];
        return (
          relatesTo?.rel_type === RelationType.Replace &&
          !!relatesTo.event_id &&
          pinnedEventIds.includes(relatesTo.event_id) &&
          event.getRoomId() === roomId
        );
      })
    );
  }

  /********************************************************************************************************************
   * Fetching
   ********************************************************************************************************************/

  /**
   * Holt ein gepinntes Event: aus dem lokalen Cache (fetchAndApplyRelations)
   * oder vom Server (fetchElementAndRelations).
   * Poll-Events werden zusätzlich verarbeitet.
   */
  private fetchPinnedEvent(roomId: string, eventId: string): Observable<MatrixEvent> {
    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(`Room ${roomId} not found in client cache`);
    }

    const event = room.findEventById(eventId);
    if (event) {
      return this.fetchAndApplyRelations(roomId, eventId, event);
    }

    return this.fetchElementAndRelations(roomId, eventId).pipe(
      switchMap(({ event, relations }) => {
        this.replaceContentIfNeeded(event, relations);
        if (M_POLL_START.matches(event.getType())) {
          return this.processPollEvents(room, event).pipe(map(() => event));
        }
        return of(event);
      })
    );
  }

  /**
   * Für Events, die bereits im lokalen Cache liegen (findEventById):
   * 1. Neuesten Edit (m.replace) vom Server nachladen und auf dem Event-Objekt anwenden.
   * 2. Entschlüsselung versuchen — falls die Keys noch fehlen, greift der fromMatrixEvent-Trigger
   *    in loadAndMapPinnedMessages$ als Fallback.
   */
  private fetchAndApplyRelations(roomId: string, eventId: string, event: MatrixEvent): Observable<MatrixEvent> {
    return from(
      this.matrix.client.relations(roomId, eventId, RelationType.Replace, null, {
        limit: 1,
        dir: Direction.Backward
      })
    ).pipe(
      switchMap(result => {
        this.replaceContentIfNeeded(event, result.events);
        return from(this.matrix.client.decryptEventIfNeeded(event, { emit: false })).pipe(map(() => event));
      })
    );
  }

  /**
   * Für Events, die NICHT im lokalen Cache liegen:
   * Holt Event und neuesten Edit (m.replace) parallel vom Server (forkJoin),
   * da beide Requests unabhängig sind. Anschließend Entschlüsselung versuchen.
   * Promises werden mit from() gewrappt, da forkJoin nur mit completing Observables funktioniert.
   */
  private fetchElementAndRelations(
    roomId: string,
    eventId: string
  ): Observable<{ event: MatrixEvent; relations: MatrixEvent[] }> {
    return forkJoin({
      iEvent: from(this.matrix.client.fetchRoomEvent(roomId, eventId)),
      // Backward-Sortierung damit limit:1 den neuesten Edit liefert statt den ältesten
      relations: from(
        this.matrix.client.relations(roomId, eventId, RelationType.Replace, null, { limit: 1, dir: Direction.Backward })
      )
    }).pipe(
      switchMap(({ iEvent, relations }) => {
        const event = new MatrixEvent(iEvent);
        return from(this.matrix.client.decryptEventIfNeeded(event, { emit: false })).pipe(
          map(() => ({ event, relations: relations.events }))
        );
      })
    );
  }

  /********************************************************************************************************************
   * Mapping (MatrixEvent → PinnedMessageDTO)
   ********************************************************************************************************************/

  /** Mappt ein MatrixEvent zu einem PinnedMessageDTO. Berücksichtigt Edits, Redactions und unentschlüsselte Events. */
  private eventToPinnedMessage(event: MatrixEvent): PinnedMessageDTO {
    if (event.isRedacted()) {
      return this.getPinDtoForRedactionEvent(event);
    }

    const replaceEvent = event.replacingEvent();
    const clearContent = replaceEvent
      ? (replaceEvent.getClearContent()?.['m.new_content'] ?? event.getClearContent())
      : event.getClearContent();
    if (!clearContent) {
      return this.getUndecryptedPinnedMessage(event);
    }

    const { senderId, sender } = this.getSender(event);

    return {
      messageId: event.getId() ?? '',
      senderId,
      sender,
      msgType: clearContent.msgtype ?? 'undefined',
      formattedBody: clearContent['formatted_body'] ?? clearContent['body'],
      body: clearContent['body'] ?? clearContent['formatted_body'],
      mentions: clearContent['m.mentions']?.user_ids ?? [],
      date: event.getDate()
    };
  }

  /** Erzeugt ein PinnedMessageDTO für ein gelöschtes (redacted) Event. */
  private getPinDtoForRedactionEvent(event: MatrixEvent): PinnedMessageDTO {
    const redactionEvent = event.getRedactionEvent();
    if (!redactionEvent) {
      throw new Error('getPinDtoForRedactionEvent: Redacted Event hat kein RedactionEvent');
    }

    const { senderId, sender } = this.getSender(event);
    return {
      messageId: event.getId() ?? '',
      senderId,
      sender,
      msgType: redactionEvent.type ?? 'undefined',
      formattedBody: redactionEvent.content['reason'] ?? '',
      body: redactionEvent.content['reason'] ?? '',
      mentions: [],
      date: event.getDate()
    };
  }

  /** Erzeugt ein Platzhalter-DTO für Events, die noch nicht entschlüsselt werden konnten. */
  private getUndecryptedPinnedMessage(event: MatrixEvent): PinnedMessageDTO {
    return {
      messageId: event.getId() ?? '',
      senderId: event.sender?.userId ?? 'Unbekannt',
      sender: event.sender?.rawDisplayName ?? 'Unbekannt',
      msgType: event.getType(),
      formattedBody: '',
      body: '',
      mentions: [],
      date: event.getDate()
    };
  }

  /********************************************************************************************************************
   * Helpers
   ********************************************************************************************************************/

  /** Sendet den aktualisierten Pin-State (m.room.pinned_events) an den Server. */
  private async sendPinState(roomId: string, pinIds: string[]): Promise<void> {
    await this.matrix.client.sendStateEvent(roomId, EventType.RoomPinnedEvents, {
      pinned: pinIds
    });
  }

  /** Prüft, ob der aktuelle Nutzer Pin-Rechte im Raum hat. Zeigt bei fehlender Berechtigung eine Snackbar. */
  private checkPinRights(room: Room, errorMessage: string): boolean {
    const userId = this.matrix.getUserId();
    if (!this.roomPowerLevelService.canDoEvent(EventType.RoomPinnedEvents, room.powerLevels, userId)) {
      this.snackbarService.error(errorMessage);
      return false;
    }
    return true;
  }

  /** Extrahiert Sender-ID und Anzeigename aus einem MatrixEvent. */
  private getSender(event: MatrixEvent): { sender: string; senderId: string } {
    const senderId = event.getSender();
    if (!senderId) {
      throw new Error('getSender: Event hat keinen Sender');
    }
    const sender = event.sender?.rawDisplayName ?? this.extractLocalpart(senderId);
    return { sender, senderId };
  }

  /** Extrahiert den Localpart aus einer Matrix-ID (@localpart:homeserver → localpart). */
  private extractLocalpart(mxid: string): string {
    const colonIndex = mxid.indexOf(':');
    if (!mxid.startsWith('@') || colonIndex === -1) {
      return mxid;
    }
    return mxid.substring(1, colonIndex);
  }

  /** Wendet den neuesten Edit (m.replace) auf ein Event an, falls vorhanden. */
  private replaceContentIfNeeded(event: MatrixEvent, relations: MatrixEvent[]): void {
    if (relations?.length > 0) {
      event.makeReplaced(relations[0]);
    }
  }

  /**
   * Verarbeitet Poll-Events im Kontext eines Raums, damit die Abstimmungsdaten verfügbar sind.
   *
   * room.processPollEvents([event]) ist eine Methode des Matrix-SDK, die das Poll-Event im Kontext des Raums
   * verarbeitet — dabei werden die Abstimmungsergebnisse (Antworten, Stimmen) aus den zugehörigen Relations geladen und
   * dem Event zugeordnet. Ohne diesen Aufruf würde eine gepinnte Umfrage nur als rohes Event ohne aufbereitete
   * Abstimmungsdaten angezeigt werden.
   * Kurz: Wenn jemand eine Umfrage pinnt, sorgt processPollEvents dafür, dass die Umfrageergebnisse korrekt dargestellt
   * werden.
   */
  private processPollEvents(room: MatrixRoom, event: MatrixEvent): Observable<void> {
    return from(room.processPollEvents([event]));
  }
}

export interface PinnedMessageDTO {
  messageId: string;
  senderId: string;
  sender: string;
  msgType: EventType | string;
  formattedBody: string;
  body: string;
  mentions: string[];
  date: Date | null;
}
